import { useState, useEffect } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import {
  AlertCircle,
  BookOpen,
  ChevronLeft,
  CheckCircle,
  XCircle,
  RotateCcw,
  Brain,
  Target,
  Award,
  TrendingUp,
  Clock,
  Flag,
  Home,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import './StudentQuiz.css'
import {
  getQuizById,
  submitQuizAnswers,
  getStudentByUserId,
} from "../../../../services/MyQuizzesService";
import { getMe } from "../../../../services/AuthService";

import { toast } from '../../common/Toast/toast';
import { useAuth } from "../../../../context/AuthContext";
import { confirm } from '../../common/Confirm/confirm';

const StudentQuiz = () => {
  const [quiz, setQuiz] = useState(null);
  const [mcqs, setMcqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMcqIndex, setCurrentMcqIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showAnswers, setShowAnswers] = useState(false);
  const [showScore, setShowScore] = useState(false);
  const [furthestReached, setFurthestReached] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [quizStartTime, setQuizStartTime] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quizId, setQuizId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [studentId, setStudentId] = useState(null);
  const [studentName, setStudentName] = useState(null);
  const [quizResult, setQuizResult] = useState(null); // State to store quiz results

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const storedQuizId = sessionStorage.getItem("currentQuizId");
    const storedAdminViewingStudentId = sessionStorage.getItem(
      "adminViewingStudentId",
    );
    const storedStudentName = sessionStorage.getItem("currentStudentName");

    if (storedQuizId) {
      setQuizId(storedQuizId);
    } else {
      setError("No quiz selected. Please go back and select a quiz.");
      setLoading(false);
    }

    // For admin viewing a specific student, use the stored student ID directly
    if (user?.isSuperAdmin && storedAdminViewingStudentId) {
      setStudentId(storedAdminViewingStudentId);
      setStudentName(storedStudentName);
    }
  }, [user?.isSuperAdmin]);

  useEffect(() => {
    let interval = null;
    if (timerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((time) => {
          if (time <= 1) {
            handleTimeUp();
            return 0;
          }
          if (time === 300) {
            toast.warning("5 minutes remaining!");
          }
          return time - 1;
        });
      }, 1000);
    } else if (!timerActive) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timerActive, timeLeft]);

  const fetchStudentData = async () => {
    try {
      // Case 1: Admin is viewing a specific student - studentId is already set
      if (user?.isSuperAdmin && studentId) {
        // No need to fetch, we already have the student ID
        return { id: studentId, name: studentName };
      }

      // Case 2: Regular student is taking quiz - need to fetch by userId
      else if (!user?.isSuperAdmin) {
        const loggedInUserId = currentUser?.id;
        if (!loggedInUserId) {
          throw new Error("User ID not found");
        }
        const response = await getStudentByUserId(loggedInUserId);
        if (response?.data) {
          return response.data;
        }
        return response;
      }
      // Case 3: Admin with no student selected (should not happen)
      else {
        throw new Error("No student selected for admin view");
      }
    } catch (error) {
      console.error("Error fetching student data:", error);
      throw new Error("Failed to fetch student information");
    }
  };

  const fetchQuizData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!quizId) {
        setError("No quiz selected. Please go back and select a quiz.");
        return;
      }

      // Get current user if not already available
      let loggedInUser = user;
      if (!loggedInUser?.id) {
        const meResponse = await getMe();
        loggedInUser = meResponse.data;
      }
      setCurrentUser(loggedInUser);

      if (!loggedInUser?.id) {
        setError("Please log in to access this quiz.");
        return;
      }

      // Fetch student data based on user type
      const student = await fetchStudentData();
      if (!student || !student.id) {
        setError("Student profile not found. Please contact support.");
        return;
      }

      // If admin and we already have studentId, we don't need to set it again
      if (!user?.isSuperAdmin) {
        setStudentId(student.id);
        setStudentName(student.name);
      }

      // Fetch quiz data
      const response = await getQuizById(quizId);

      let quizData;
      if (response?.data) {
        quizData = response.data;
      } else {
        quizData = response;
      }

      if (!quizData) {
        setError("Quiz not found or you don't have access to this quiz.");
        return;
      }

      setQuiz(quizData);

      // Get MCQs from the response directly
      let mcqsList = [];
      if (quizData.mcqIds && Array.isArray(quizData.mcqIds)) {
        mcqsList = quizData.mcqIds;
      }

      if (mcqsList.length === 0) {
        setError("No questions available for this quiz.");
        return;
      }

      // Shuffle MCQs for randomization
      const shuffledMcqs = shuffleArray(mcqsList);
      setMcqs(shuffledMcqs);

      // Initialize selected answers
      const initialAnswers = {};
      shuffledMcqs.forEach((_, index) => {
        initialAnswers[index] = null;
      });
      setSelectedAnswers(initialAnswers);

      // Set timer
      const quizDurationInSeconds = (quizData.time || 30) * 60;
      setTimeLeft(quizDurationInSeconds);
      setTimerActive(true);
      setQuizStartTime(new Date());

      setError(null);
    } catch (err) {
      console.error("Error fetching quiz:", err);
      setError("Failed to load quiz. Please try again later.");
      toast.error("Failed to load quiz. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (quizId) {
      fetchQuizData();
    }
  }, [quizId]);

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const handleAnswerSelect = (mcqIndex, selectedOption, optionIndex) => {
    if (showAnswers || selectedAnswers[mcqIndex] !== null) return;

    setSelectedAnswers((prev) => ({
      ...prev,
      [mcqIndex]: optionIndex + 1, // Store as 1-based index (1, 2, 3, 4)
    }));

    if (mcqIndex > furthestReached) {
      setFurthestReached(mcqIndex);
    }
  };

  const handleMcqNavigation = (index) => {
    if (index <= furthestReached) {
      setCurrentMcqIndex(index);
    }
  };

  const handleNextMcq = () => {
    const nextIndex = currentMcqIndex + 1;
    if (nextIndex < mcqs.length) {
      setCurrentMcqIndex(nextIndex);
      if (nextIndex > furthestReached) {
        setFurthestReached(nextIndex);
      }
    }
  };

  const handlePreviousMcq = () => {
    if (currentMcqIndex > 0) {
      setCurrentMcqIndex(currentMcqIndex - 1);
    }
  };

  const handleTimeUp = async () => {
    await handleFinishTest(true);
  };

  const calculateScore = () => {
    let correct = 0;
    let earnedMarks = 0;
    const totalMarks = quiz?.marks ? Number(quiz.marks) : mcqs.length;
    const marksPerMcq = totalMarks / mcqs.length;

    mcqs.forEach((mcq, index) => {
      if (selectedAnswers[index] && mcq) {
        // Get the option text based on selected index (1-based)
        const selectedOptionText = getOptionTextByIndex(
          mcq,
          selectedAnswers[index],
        );
        const isCorrect = selectedOptionText === mcq.correctAnswer;
        if (isCorrect) {
          correct++;
          earnedMarks += marksPerMcq;
        }
      }
    });

    const percentage = Math.round((earnedMarks / totalMarks) * 100);
    const resultStatus = percentage >= 40 ? "Pass" : "Fail";

    return {
      correct,
      total: mcqs.length,
      percentage,
      earnedMarks: Math.round(earnedMarks * 100) / 100,
      totalMarks,
      marksPerMcq: Math.round(marksPerMcq * 100) / 100,
      result: resultStatus,
    };
  };

  const getOptionTextByIndex = (mcq, optionIndex) => {
    switch (optionIndex) {
      case 1:
        return mcq.option1;
      case 2:
        return mcq.option2;
      case 3:
        return mcq.option3;
      case 4:
        return mcq.option4;
      default:
        return null;
    }
  };

  const handleFinishTest = async (autoSubmit = false) => {
    if (isSubmitting) return;

    if (!autoSubmit) {
      const result = await confirm({
        title: "Submit Quiz?",
        text: "Are you sure you want to submit your quiz? This action cannot be undone.",
        confirmButtonText: "Yes, Submit!",
      });

      if (!result.isConfirmed) return;
    }

    setIsSubmitting(true);

    try {
      const endTime = new Date();
      const timeTaken = Math.floor((endTime - quizStartTime) / 1000);

      // Format time taken as MM:SS
      const minutes = Math.floor(timeTaken / 60);
      const seconds = timeTaken % 60;
      const formattedTimeTaken = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

      // Prepare answers in the required format
      const formattedAnswers = [];
      mcqs.forEach((mcq, index) => {
        if (mcq.id && selectedAnswers[index] !== null) {
          formattedAnswers.push({
            mcqId: mcq.id,
            selectedAnswer: selectedAnswers[index], // This is already 1-based index
          });
        }
      });

      const submissionData = {
        quizId: quizId,
        studentId: studentId, // Use the student ID directly
        answers: formattedAnswers,
        timeConsumed: formattedTimeTaken,
        outOf: quiz?.marks ? Number(quiz.marks) : mcqs.length,
      };

      // Get the submission response
      const submissionResponse = await submitQuizAnswers(submissionData);

      // Store the quiz result from the response
      let resultData;
      if (submissionResponse?.data) {
        resultData = submissionResponse.data;
      } else {
        resultData = submissionResponse;
      }

      setQuizResult(resultData);
      setTimerActive(false);
      setShowScore(true);
      setShowAnswers(true);

      toast.success("Quiz submitted successfully!");
    } catch (error) {
      console.error("Error submitting quiz:", error);
      toast.error("Failed to submit quiz. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetPractice = () => {
    setSelectedAnswers({});
    setShowAnswers(false);
    setShowScore(false);
    setCurrentMcqIndex(0);
    setFurthestReached(0);
    const quizDurationInSeconds = (quiz?.time || 30) * 60;
    setTimeLeft(quizDurationInSeconds);
    setTimerActive(true);
    setQuizStartTime(new Date());
    const resetAnswers = {};
    mcqs.forEach((_, index) => {
      resetAnswers[index] = null;
    });
    setSelectedAnswers(resetAnswers);
    setQuizResult(null); // Clear quiz results
  };

  const getOptionClassName = (mcqIndex, option, optionIndex) => {
    const currentMcq = mcqs[mcqIndex];
    if (!currentMcq) return "option-card";

    const selectedOptionIndex = selectedAnswers[mcqIndex];
    const isSelected = selectedOptionIndex === optionIndex + 1;

    if (showAnswers && quizResult) {
      // Use quizResult to determine correctness
      const mcqId = currentMcq.id;
      const answerData = quizResult.answers?.find((a) => a.mcqId === mcqId);
      const isCorrect = answerData?.isCorrect || false;
      const isIncorrect = isSelected && !isCorrect;

      if (isCorrect && option === currentMcq.correctAnswer)
        return "option-card option-correct";
      if (isIncorrect) return "option-card option-incorrect";
    } else if (showAnswers) {
      // Fallback to local calculation if quizResult is not available
      const isCorrect = option === currentMcq.correctAnswer;
      const isIncorrect = isSelected && option !== currentMcq.correctAnswer;
      if (isCorrect) return "option-card option-correct";
      if (isIncorrect) return "option-card option-incorrect";
    }

    if (isSelected) return "option-card option-selected";
    return "option-card";
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Container fluid>
          <div className="loading-content">
            <div className="loading-spinner">
              <div className="spinner-ring"></div>
              <Brain size={40} className="brain-icon" />
            </div>
            <h3 className="loading-title">Preparing Your Quiz Session</h3>
            <p className="loading-subtitle">
              Loading quiz questions and setting up your assessment
            </p>
            <div className="loading-progress">
              <div className="progress-bar"></div>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          backgroundColor: "#f8f9fa",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "20px",
          textAlign: "center",
        }}
      >
        <Container fluid>
          <div>
            <div style={{ marginBottom: "20px" }}>
              <AlertCircle size={60} color="#f97316" />
            </div>

            <h2 style={{ marginBottom: "10px", fontWeight: "600" }}>
              Quiz Not Available
            </h2>

            <p style={{ marginBottom: "25px", color: "#6c757d" }}>{error}</p>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <Button
                onClick={() => navigate("/student-quizzes")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 18px",
                  backgroundColor: "#f97316",
                  border: "none",
                  color: "#fff",
                  borderRadius: "6px",
                }}
              >
                <Home size={18} />
                Back to Dashboard
              </Button>

              <Button
                onClick={fetchQuizData}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 18px",
                  backgroundColor: "transparent",
                  border: "1px solid #f97316",
                  color: "#f97316",
                  borderRadius: "6px",
                }}
              >
                <RotateCcw size={18} />
                Try Again
              </Button>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (!mcqs || mcqs.length === 0) {
    return (
      <div className="error-container">
        <Container fluid>
          <div className="error-content">
            <div className="error-icon">
              <AlertCircle size={60} />
            </div>
            <h2 className="error-title">No Questions Available</h2>
            <p className="error-message">
              This quiz doesn't have any questions yet.
            </p>
            <div className="error-actions">
              <Button
                onClick={() => navigate("/student-quizzes")}
                className="btn-primary-custom"
              >
                <ChevronLeft size={18} />
                Back to My Quizzes
              </Button>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  const currentMcq = mcqs[currentMcqIndex];
  const answeredCount = Object.values(selectedAnswers).filter(
    (answer) => answer !== null,
  ).length;

  return (
    <div className="mcq-container">      {/* Enhanced Header */}
      <div className="header-section">
        <Container>
          <Row className="align-items-center">
            <Col md={6}>
              <div className="header-content">
                <div className="course-info">
                  <h4 className="course-title">
                    {quiz?.title ||
                      sessionStorage.getItem("currentQuizTitle") ||
                      "Quiz"}
                  </h4>
                  <p className="course-subtitle text-light">
                    <Brain size={16} className="me-2" />
                    {quiz?.courseId?.[0]?.name || "Course"} • Quiz Assessment •
                    <br />
                    Total:{" "}
                    {quizResult
                      ? quizResult.totalMarks
                      : quiz?.marks
                        ? Number(quiz.marks)
                        : mcqs.length}{" "}
                    marks
                    {user?.isSuperAdmin && studentId && (
                      <span className="ms-2 badge bg-warning text-dark">
                        Admin View: {studentName || "Student"}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="header-stats">
                <div className="stat-item text-light">
                  <Clock size={16} />
                  <span>
                    {!showScore
                      ? formatTime(timeLeft)
                      : quizResult?.timeConsumed || "00:00"}
                  </span>
                </div>
                <div className="stat-item">
                  <Target size={16} />
                  <span>
                    {currentMcqIndex + 1}/{mcqs.length}
                  </span>
                </div>
                <div className="stat-item">
                  <TrendingUp size={16} />
                  <span>{answeredCount} answered</span>
                </div>
                {showScore && quizResult && (
                  <div className="stat-item score-badge">
                    <Award size={16} />
                    <span>
                      {quizResult.earnedMarks}/{quizResult.totalMarks} (
                      {quizResult.percentage}
                      %)
                    </span>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="main-content">
        <Row className="g-4">
          {/* Enhanced Sidebar */}
          <Col lg={3}>
            <div className="sidebar-card">
              <div className="sidebar-header">
                <BookOpen size={20} />
                <h6>Question Navigator</h6>
              </div>
              <div className="progress-overview">
                <div className="progress-stats">
                  <div className="stat">
                    <span className="stat-number">{answeredCount}</span>
                    <span className="stat-label">Answered</span>
                  </div>
                  <div className="stat">
                    <span className="stat-number">
                      {mcqs.length - answeredCount}
                    </span>
                    <span className="stat-label">Remaining</span>
                  </div>
                </div>
                <div className="progress-bar-container">
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${(answeredCount / mcqs.length) * 100}%` }}
                  ></div>
                </div>
              </div>
              <div className="questions-grid">
                {mcqs.map((_, index) => {
                  const isAnswered = selectedAnswers[index] !== null;
                  const isCurrent = index === currentMcqIndex;
                  const isCorrect =
                    showScore &&
                    quizResult &&
                    quizResult.answers &&
                    quizResult.answers.find((a) => a.mcqId === mcqs[index]?.id)
                      ?.isCorrect;
                  const isAccessible = index <= furthestReached;

                  return (
                    <button
                      key={index}
                      className={`question-btn ${isCurrent ? "current" : ""} ${
                        isAnswered
                          ? showScore
                            ? isCorrect
                              ? "correct"
                              : "incorrect"
                            : "answered"
                          : "unanswered"
                      } ${!isAccessible ? "locked" : ""}`}
                      onClick={() => handleMcqNavigation(index)}
                      disabled={!isAccessible}
                    >
                      <span className="question-number">{index + 1}</span>
                      {showScore && isAnswered && (
                        <div className="question-status">
                          {isCorrect ? (
                            <CheckCircle size={12} />
                          ) : (
                            <XCircle size={12} />
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="sidebar-actions">
                {!showScore && (
                  <Button
                    className="action-btn finish-btn"
                    onClick={handleFinishTest}
                    disabled={isSubmitting}
                  >
                    <Flag size={16} />
                    {isSubmitting ? "Submitting..." : "Submit Quiz"}
                  </Button>
                )}
              </div>
            </div>
          </Col>

          {/* Enhanced Main Content */}
          <Col lg={9}>
            {showScore && quizResult ? (
              <div className="score-card">
                <div className="score-header">
                  <Award size={32} />
                  <h3>Quiz Results</h3>
                </div>
                <div className="score-content">
                  <div className="score-metric">
                    <div className="score-value">
                      {quizResult.earnedMarks}/{quizResult.totalMarks}
                    </div>
                    <div className="score-label">
                      Marks Earned ({quizResult.percentage}%)
                    </div>
                  </div>
                  <div className="score-details">
                    <div className="detail-item correct">
                      <CheckCircle size={16} />
                      <span>
                        {quizResult.scored} Correct (
                        {(
                          quizResult.scored * quizResult.marksPerQuestion
                        ).toFixed(1)}{" "}
                        marks)
                      </span>
                    </div>
                    <div className="detail-item incorrect">
                      <XCircle size={16} />
                      <span>
                        {quizResult.totalQuestions - quizResult.scored}{" "}
                        Incorrect
                      </span>
                    </div>
                    <div className="detail-item total">
                      <BookOpen size={16} />
                      <span>
                        {quizResult.totalQuestions} Total Questions (
                        {quizResult.marksPerQuestion} marks each)
                      </span>
                    </div>
                  </div>
                  {/* Quiz Results List */}
                  <div className="results-list">
                    {mcqs.map((mcq, index) => {
                      // Find the answer data from quizResult
                      const answerData = quizResult.answers?.find(
                        (a) => a.mcqId === mcq.id,
                      );
                      const isCorrect = answerData?.isCorrect || false;
                      const selectedOptionIndex = answerData?.selectedAnswer;
                      const selectedOptionText = selectedOptionIndex
                        ? getOptionTextByIndex(mcq, selectedOptionIndex)
                        : null;
                      const correctAnswer = mcq.correctAnswer;
                      const marksObtained = answerData?.marksObtained || 0;

                      return (
                        <div
                          key={index}
                          className={`result-item ${isCorrect ? "correct" : "incorrect"}`}
                        >
                          <div className="result-question">
                            <span className="result-number">Q{index + 1}:</span>
                            <span className="result-text">
                              {mcq.question || "Loading..."}
                            </span>
                            <span className="result-marks">
                              {isCorrect ? `+${marksObtained}` : "0"} marks
                            </span>
                          </div>
                          <div className="result-answers">
                            <div className="result-answer">
                              <span className="answer-label">Your Answer:</span>
                              <span
                                className={`answer-value ${!isCorrect ? "wrong" : ""}`}
                              >
                                {selectedOptionText || "Not answered"}
                              </span>
                            </div>
                            {!isCorrect && correctAnswer && (
                              <div className="result-answer">
                                <span className="answer-label">
                                  Correct Answer:
                                </span>
                                <span className="answer-value correct">
                                  {correctAnswer}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="score-actions">
                  <Button
                    className="action-btn restart-btn"
                    onClick={() => navigate("/student-quizzes")}
                  >
                    <Home size={16} />
                    Back to Dashboard
                  </Button>
                </div>
              </div>
            ) : (
              <div className="question-card">
                <div className="question-header">
                  <div className="question-meta">
                    <span className="question-label">
                      Question {currentMcqIndex + 1}
                    </span>
                    <div className="difficulty-badge">
                      <Brain size={14} />
                      Quiz Mode (
                      {quiz?.marks
                        ? (Number(quiz.marks) / mcqs.length).toFixed(2)
                        : "1"}{" "}
                      marks)
                    </div>
                  </div>
                  {showAnswers && quizResult && (
                    <div
                      className={`answer-status ${
                        quizResult.answers?.find(
                          (a) => a.mcqId === currentMcq?.id,
                        )?.isCorrect
                          ? "correct"
                          : "incorrect"
                      }`}
                    >
                      {quizResult.answers?.find(
                        (a) => a.mcqId === currentMcq?.id,
                      )?.isCorrect ? (
                        <>
                          <CheckCircle size={16} />
                          Correct Answer (+{quizResult.marksPerQuestion} marks)
                        </>
                      ) : (
                        <>
                          <XCircle size={16} />
                          Incorrect Answer (0 marks)
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="question-content">
                  <h3 className="question-text">{currentMcq?.question}</h3>
                  <div className="options-container">
                    {[
                      currentMcq?.option1,
                      currentMcq?.option2,
                      currentMcq?.option3,
                      currentMcq?.option4,
                    ].map((option, optionIndex) => {
                      const canSelect =
                        !showAnswers &&
                        selectedAnswers[currentMcqIndex] === null;
                      return option ? (
                        <div
                          key={optionIndex}
                          className={getOptionClassName(
                            currentMcqIndex,
                            option,
                            optionIndex,
                          )}
                          onClick={() =>
                            handleAnswerSelect(
                              currentMcqIndex,
                              option,
                              optionIndex,
                            )
                          }
                          style={{
                            cursor: canSelect ? "pointer" : "not-allowed",
                            opacity: !canSelect && !showAnswers ? 0.6 : 1,
                          }}
                        >
                          <div className="option-content">
                            <div className="option-letter">
                              {String.fromCharCode(65 + optionIndex)}
                            </div>
                            <span className="option-text">{option}</span>
                          </div>
                          {showAnswers && quizResult && (
                            <div className="option-status">
                              {option === currentMcq?.correctAnswer && (
                                <CheckCircle
                                  size={20}
                                  className="correct-icon"
                                />
                              )}
                              {selectedAnswers[currentMcqIndex] ===
                                optionIndex + 1 &&
                                option !== currentMcq?.correctAnswer && (
                                  <XCircle
                                    size={20}
                                    className="incorrect-icon"
                                  />
                                )}
                            </div>
                          )}
                        </div>
                      ) : null;
                    })}
                  </div>
                  {showAnswers && quizResult && (
                    <div className="answer-explanation">
                      <div className="explanation-header">
                        <CheckCircle size={20} />
                        <h6>Answer Explanation</h6>
                      </div>
                      <div className="explanation-content">
                        <p>
                          <strong>Correct Answer:</strong>{" "}
                          {currentMcq?.correctAnswer}
                        </p>
                        {selectedAnswers[currentMcqIndex] && (
                          <p>
                            <strong>Your Answer:</strong>{" "}
                            {getOptionTextByIndex(
                              currentMcq,
                              selectedAnswers[currentMcqIndex],
                            )}
                          </p>
                        )}
                        <p>
                          <strong>Marks:</strong>{" "}
                          {quizResult.answers?.find(
                            (a) => a.mcqId === currentMcq?.id,
                          )?.marksObtained || 0}{" "}
                          / {quizResult.marksPerQuestion}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="question-footer">
                  <Button
                    className="nav-btn prev-btn"
                    onClick={handlePreviousMcq}
                    disabled={currentMcqIndex === 0}
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </Button>
                  <div className="question-indicator">
                    <span
                      className={`indicator-dot ${selectedAnswers[currentMcqIndex] ? "answered" : "unanswered"}`}
                    ></span>
                    <span className="indicator-text">
                      {selectedAnswers[currentMcqIndex]
                        ? "Answered"
                        : "Not answered"}
                    </span>
                  </div>
                  <Button
                    className="nav-btn next-btn"
                    onClick={handleNextMcq}
                    disabled={currentMcqIndex === mcqs.length - 1}
                  >
                    Next
                    <ChevronLeft
                      size={16}
                      style={{ transform: "rotate(180deg)" }}
                    />
                  </Button>
                </div>
              </div>
            )}
          </Col>
        </Row>
      </Container>

      
    </div>
  );
};

export default StudentQuiz;
